/**
 * Product Bundle Box
 * - Syncs price, compare-at, savings and sticky bar with the selected bundle radio
 * - Ajax add to cart through window.CartApi (falls back to the native form post)
 * - Shows the mobile sticky order bar while the buy box is out of view
 */

if (!customElements.get('product-bundle-box')) {
  class ProductBundleBox extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-bundle-form]');
      if (!this.form) return;

      this.priceEl = this.querySelector('[data-bundle-price]');
      this.compareEl = this.querySelector('[data-bundle-compare]');
      this.saveEl = this.querySelector('[data-bundle-save]');
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.submitLabel = this.querySelector('[data-bundle-submit-label]');
      this.sticky = this.querySelector('[data-bundle-sticky]');
      this.stickyPrice = this.querySelector('[data-bundle-sticky-price]');
      this.stickySubmit = this.querySelector('[data-bundle-sticky-submit]');

      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleStickySubmit = this.handleStickySubmit.bind(this);

      this.form.addEventListener('change', this.handleChange);
      this.form.addEventListener('submit', this.handleSubmit);
      this.stickySubmit?.addEventListener('click', this.handleStickySubmit);

      this.update();
      this.initSticky();
      this.initGallery();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.handleChange);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.stickySubmit?.removeEventListener('click', this.handleStickySubmit);
      this.thumbList?.removeEventListener('click', this.handleThumbClick);
      this.stickyObserver?.disconnect();
      this.stickyObserver = null;
    }

    initGallery() {
      this.thumbList = this.querySelector('[data-bundle-thumbs]');
      if (!this.thumbList) return;

      const card = this.querySelector('.product-bundle__media-card');
      this.galleryImages = card ? Array.from(card.querySelectorAll('.product-bundle__image')) : [];
      if (this.galleryImages.length < 2) return;

      this.handleThumbClick = this.handleThumbClick.bind(this);
      this.thumbList.addEventListener('click', this.handleThumbClick);
    }

    handleThumbClick(event) {
      const thumb = event.target.closest('[data-bundle-thumb]');
      if (!thumb) return;

      const index = Number(thumb.dataset.bundleThumb);
      if (Number.isNaN(index) || !this.galleryImages[index]) return;

      this.galleryImages.forEach((image, i) => {
        image.classList.toggle('is-active', i === index);
      });

      this.thumbList.querySelectorAll('[data-bundle-thumb]').forEach((button) => {
        const isActive = button === thumb;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', String(isActive));
      });

      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    getSelectedInput() {
      return this.form.querySelector('input[name="id"]:checked');
    }

    handleChange(event) {
      if (event.target.matches('input[name="id"]')) {
        this.update();
      }
    }

    update() {
      const input = this.getSelectedInput();
      if (!input) return;

      const { price, compare, save, title } = input.dataset;
      const available = input.dataset.available === 'true';

      if (this.priceEl) this.priceEl.innerHTML = price;

      if (this.compareEl) {
        this.compareEl.innerHTML = compare;
        this.compareEl.hidden = !compare;
      }

      if (this.saveEl) {
        this.saveEl.innerHTML = save;
        this.saveEl.hidden = !save;
      }

      if (this.submitButton && this.submitLabel) {
        this.submitButton.disabled = !available;
        this.submitLabel.textContent = available
          ? this.submitButton.dataset.labelAvailable
          : this.submitButton.dataset.labelSoldOut;
      }

      if (this.stickySubmit) this.stickySubmit.disabled = !available;

      if (this.stickyPrice) {
        this.stickyPrice.innerHTML = title ? `${price} (${title})` : price;
      }
    }

    async handleSubmit(event) {
      if (this.dataset.afterAdd === 'checkout') {
        this.checkoutSelected(event);
        return;
      }

      if (!window.CartApi?.addToCart || !window.CartApi?.refreshCart) return;

      event.preventDefault();
      if (!this.submitButton || this.submitButton.disabled) return;

      this.submitButton.disabled = true;
      this.submitButton.classList.add('loading');

      // Open the drawer immediately with a spinner; the cart:added render below replaces it
      const drawer = window.FloatingHeaderCart || window.SimpleHeaderCart;
      drawer?.showAdding?.();

      try {
        // Let the drawer paint before starting the request: Shopify analytics and apps
        // hook fetch('/cart/add') and can block the main thread for hundreds of ms
        await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve)));

        const formData = new FormData(this.form);
        const selected = this.getSelectedInput();
        const cartResponse = await window.CartApi.addToCart(formData, {
          variantData: selected || undefined,
        });

        const cart = await window.CartApi.refreshCart(cartResponse);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: cart || cartResponse }));

        if (!drawer?.showAdding && !window.SimpleHeaderCart?.open) {
          (
            document.getElementById('simple-header-cart-trigger') ||
            document.getElementById('custom-header-cart-trigger')
          )?.click();
        }
      } catch (error) {
        drawer?.cancelAdding?.();
        window.CartApi.showToast(error.message, true);
      } finally {
        this.submitButton.classList.remove('loading');
        const selected = this.getSelectedInput();
        this.submitButton.disabled = selected ? selected.dataset.available !== 'true' : false;
      }
    }

    /**
     * Checkout mode: replace the cart with only the selected bundle so earlier
     * cart items never carry over into checkout.
     */
    async checkoutSelected(event) {
      event.preventDefault();
      if (!this.submitButton || this.submitButton.disabled) return;

      const variantId = this.getSelectedInput()?.value || this.form.querySelector('[name="id"]')?.value;
      if (!variantId) return;

      const root = window.Shopify?.routes?.root || '/';
      this.submitButton.disabled = true;
      this.submitButton.classList.add('loading');

      try {
        const clearResponse = await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        });
        if (!clearResponse.ok) throw new Error('Could not update cart');

        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!addResponse.ok) {
          const error = await addResponse.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Could not add to cart');
        }

        window.location.href = `${root}checkout`;
      } catch (error) {
        if (!window.CartApi?.showToast) {
          // Cart permalink replaces the cart with just this variant and opens checkout
          window.location.href = `${root}cart/${variantId}:1`;
          return;
        }
        window.CartApi.showToast(error.message, true);
        this.submitButton.classList.remove('loading');
        const selected = this.getSelectedInput();
        this.submitButton.disabled = selected ? selected.dataset.available !== 'true' : false;
      }
    }

    // Sticky bar button runs the same add-to-cart / checkout flow as the main button
    handleStickySubmit() {
      if (!this.submitButton || this.submitButton.disabled) return;

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.submitButton);
      } else {
        this.submitButton.click();
      }
    }

    initSticky() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;

      const stickyLink = this.stickySubmit;

      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const isVisible = !entry.isIntersecting;
          this.sticky.classList.toggle('is-visible', isVisible);
          this.sticky.setAttribute('aria-hidden', String(!isVisible));
          if (stickyLink) stickyLink.tabIndex = isVisible ? 0 : -1;
        },
        { threshold: 0 }
      );

      this.stickyObserver.observe(this.form);
    }
  }

  customElements.define('product-bundle-box', ProductBundleBox);
}
